import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from "react";
import { SidebarContext } from "../../context/SidebarContext";
import SectionMenuLeft from "../../components/SectionMenuLeft/SectionMenuLeft.jsx";
import { config } from "../../config.js";
import ReactTable from "../../components/ReactTable/ReactTable.jsx";
import "./CancellationPolicies.css";
import { ToastContainer, toast } from "react-toastify";
import { RiDeleteBin6Line } from "react-icons/ri";
import { LuArrowLeft, LuPencilLine } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

function CancellationPolicies() {
  const { isSidebar } = useContext(SidebarContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("user"); //  NEW

  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [retentionDays, setRetentionDays] = useState(14);
  const [retentionLoading, setRetentionLoading] = useState(true);
  const [retentionSaving, setRetentionSaving] = useState(false);

  const [formData, setFormData] = useState({
    durationValue: "",
    durationUnit: "minute",
    chargeValue: "",
    chargeType: "",
  });

  const getApiUrl = () => {
    return `${config?.backEndBaseUrl}cancellation/policies?type=${activeTab}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const method = isEditMode ? "PUT" : "POST";

      const response = await fetch(
        `${config?.backEndBaseUrl}cancellation/policies`,
        {
          method,
          headers: {
            "Content-Type": "application/json",
            "x-dashboard-request": "true",
          },
          body: JSON.stringify({
            ...formData,
            ...(isEditMode ? { policyId: editId } : {}),
            type: activeTab, //  important
          }),
        },
      );

      if (!response.ok) throw new Error();

      toast.success(
        isEditMode
          ? "Policy updated successfully"
          : "Policy added successfully",
      );

      setShowModal(false);
      setIsEditMode(false);
      setEditId(null);
      setFormData({ durationValue: "", durationUnit: "minute", chargeValue: "", chargeType: "" });

      getPolicies();
    } catch (error) {
      console.error(error);
      toast.error("Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddPolicyBtn = () => {
    setShowModal(true);
    setFormData({ durationValue: "", durationUnit: "minute", chargeValue: "", chargeType: "" });
    setIsEditMode(false);
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;

    setDeletingId(confirmDeleteId);

    try {
      const response = await fetch(
        `${config?.backEndBaseUrl}cancellation/policies`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "x-dashboard-request": "true",
          },
          body: JSON.stringify({
            policyId: confirmDeleteId,
            type: activeTab, //  important
          }),
        },
      );

      if (!response.ok) throw new Error();

      setPolicies((prev) =>
        prev.filter((policy) => policy.id !== confirmDeleteId),
      );

      toast.success("Policy deleted successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete policy");
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const getPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(getApiUrl(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-dashboard-request": "true",
        },
      });

      if (!response.ok) throw new Error();
      const result = await response.json();

      if (result.status && Array.isArray(result.policies)) {
        setPolicies(
          result.policies.map((policy) => ({
            ...policy,
          })),
        );
      } else {
        setPolicies([]);
      }
    } catch (error) {
      console.error(error);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const handleEditOpen = (policy) => {
    setFormData({
      durationValue: policy.durationValue,
      durationUnit: policy.durationUnit,
      chargeValue: policy.chargeValue,
      chargeType: policy.chargeType,
    });
    setEditId(policy.id);
    setIsEditMode(true);
    setShowModal(true);
  };

  useEffect(() => {
    getPolicies();
  }, [getPolicies]);

  useEffect(() => {
    const loadRetention = async () => {
      try {
        const response = await fetch(`${config?.backEndBaseUrl}constants/booking-media-retention`, {
          headers: { "x-dashboard-request": "true" },
        });
        const result = await response.json();
        if (response.ok && result?.data?.days) setRetentionDays(result.data.days);
      } catch (error) {
        console.error("Unable to load booking media retention", error);
      } finally {
        setRetentionLoading(false);
      }
    };
    loadRetention();
  }, []);

  const saveRetention = async () => {
    setRetentionSaving(true);
    try {
      const response = await fetch(`${config?.backEndBaseUrl}constants/booking-media-retention`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "x-dashboard-request": "true" },
        body: JSON.stringify({ days: Number(retentionDays) }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.message || "Unable to save retention");
      toast.success("Booking photo retention updated");
    } catch (error) {
      toast.error(error.message || "Unable to save retention");
    } finally {
      setRetentionSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "duration", header: "Duration" },
      { accessorKey: "chargeType", header: "Type" },
      { accessorKey: "chargeValue", header: "Value" },
      {
        header: "Actions",
        Cell: ({ row }) => {
          const policy = row.original;

          return (
            <div className="flex gap-2">
              <button
                className="btn btn-success btn-sm"
                onClick={() => handleEditOpen(policy)}
              >
                <LuPencilLine />
              </button>

              <button
                className="btn btn-danger btn-sm"
                disabled={deletingId === policy.id}
                onClick={() => setConfirmDeleteId(policy.id)}
              >
                {deletingId === policy.id ? "..." : <RiDeleteBin6Line />}
              </button>
            </div>
          );
        },
      },
    ],
    [deletingId],
  );

  return (
    <>
      <div className="body">
        <div id="wrapper">
          <div id="page">
            <div className="layout-wrap">
              <SectionMenuLeft />
              <div className="section-content-right">
                <div
                  className="main-content"
                  style={{ paddingLeft: isSidebar ? "280px" : "0px" }}
                >
                  <ToastContainer />

                  <div className="main-content-inner">
                    <div className="main-content-wrap">
                      <div className="tf-section">
                        <div className="wg-box">
                          {/*  Tabs */}
                          <div className="tab-container">
                            <div className="tab-wrapper">
                              <button
                                className={`tab-btn ${activeTab === "user" ? "active" : ""}`}
                                onClick={() => setActiveTab("user")}
                              >
                                User Policies
                              </button>

                              <button
                                className={`tab-btn ${activeTab === "driver" ? "active" : ""}`}
                                onClick={() => setActiveTab("driver")}
                              >
                                Driver Policies
                              </button>

                              <div className={`tab-indicator ${activeTab}`} />
                            </div>
                          </div>

                          <div className="booking-media-retention">
                            <div>
                              <strong>Booking photo retention</strong>
                              {/* <p>Photos in <code>cars/bookingId</code> are removed nightly after this window. The booking then receives <code>complaintWindow: false</code>.</p> */}
                            </div>
                            <div className="retention-controls">
                              <select className="form-input" value={retentionDays} disabled={retentionLoading || retentionSaving} onChange={(event) => setRetentionDays(Number(event.target.value))}>
                                <option value={7}>7 days</option>
                                <option value={14}>14 days</option>
                                <option value={30}>30 days</option>
                              </select>
                              <button className="btn btn-primary" disabled={retentionLoading || retentionSaving} onClick={saveRetention}>
                                {retentionSaving ? "Saving..." : "Save"}
                              </button>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center justify-between mb-4 gap-3">
                              <div className="flex items-center gap-3">
                                <button
                                  className="back-btn flex items-center gap-2"
                                  onClick={() => navigate("/")}
                                >
                                  <LuArrowLeft size={16} />
                                  {/* <span>Back</span> */}
                                </button>
                                <h5 className="fare-heading">
                                  {activeTab === "user"
                                    ? "User Cancellation Policies"
                                    : "Driver Cancellation Policies"}
                                </h5>
                              </div>
                            </div>

                            <button
                              className="btn btn-success"
                              onClick={handleAddPolicyBtn}
                            >
                              + Add Policy
                            </button>
                          </div>

                          {loading ? (
                            <div className="skeleton-table">
                              {[...Array(6)].map((_, i) => (
                                <div className="skeleton-row" key={i}>
                                  <div className="skeleton"></div>
                                  <div className="skeleton"></div>
                                  <div className="skeleton"></div>
                                  <div className="skeleton"></div>
                                  <div className="skeleton"></div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <ReactTable columns={columns} data={policies} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {confirmDeleteId && (
        <ConfirmModal
          loading={deletingId === confirmDeleteId}
          onCancel={() => setConfirmDeleteId(null)}
          onConfirm={handleDelete}
        />
      )}

      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h3 className="modal-title">
                {isEditMode
                  ? "Edit Cancellation Policy"
                  : "Add Cancellation Policy"}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Cancellation window</label>
                <div className="duration-fields">
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  name="durationValue"
                  value={formData.durationValue}
                  onChange={handleChange}
                  placeholder="e.g. 1"
                  className="form-input"
                  required
                />
                <select name="durationUnit" value={formData.durationUnit} onChange={handleChange} className="form-input" required>
                  <option value="minute">Minute(s)</option>
                  <option value="hour">Hour(s)</option>
                  <option value="day">Day(s)</option>
                </select>
                </div>
                <small className="duration-help">Choose Minutes, Hours, or Days. For example: 15 minutes, 1 hour, or 2 days.</small>
              </div>

              <div className="form-group">
                <label className="form-label">Charge Type</label>
                <input
                  type="text"
                  name="chargeType"
                  value={formData.chargeType}
                  onChange={handleChange}
                  placeholder="Type"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Charge Value</label>
                <input
                  type="number"
                  name="chargeValue"
                  value={formData.chargeValue}
                  onChange={handleChange}
                  placeholder="Enter value"
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => {
                  setShowModal(false);
                  setIsEditMode(false);
                  setEditId(null);
                  setFormData({
                    durationValue: "",
                    durationUnit: "minute",
                    chargeValue: "",
                    chargeType: "",
                  });
                }}
                className="btn btn-dark"
                type="button"
              >
                Cancel
              </button>

              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="btn btn-success"
              >
                {submitting
                  ? "Saving..."
                  : isEditMode
                    ? "Update Policy"
                    : "Save Policy"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const ConfirmModal = ({ onCancel, onConfirm, loading }) => (
  <div className="modal-backdrop">
    <div className="modal-card" style={{ maxWidth: 420 }}>
      <div className="modal-header">
        <h3 className="modal-title">Delete Policy</h3>
      </div>

      <div className="modal-body">
        <p>
          Are you sure you want to delete this policy?
          <br />
          <strong>This action cannot be undone.</strong>
        </p>
      </div>

      <div className="modal-footer">
        <button onClick={onCancel} className="btn btn-dark">
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="btn btn-danger"
        >
          {loading ? "Deleting..." : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

export default CancellationPolicies;
